import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { IconChevronDown, IconSearch, IconSupport, IconWhatsApp } from '../../../assets/svg/icons';
import { FieldMode } from '../../../components/atoms/Fields/enums';
import Input from '../../../components/atoms/Fields/Input';
import {
  StyledToolbar,
  StyledToolbarSearch,
} from '../../../components/molecules/Toolbar';
import useHeaderTitleContext from '../../../hooks/useHeaderTitleContext';
import routes from '../../../setup/consts/routes';
import { FAQ_ITEMS } from './consts';
import {
  StyledCategory,
  StyledCategoryDescription,
  StyledCategoryTitle,
  StyledFaqContactCTA,
  StyledFaqEmpty,
  StyledFaqHero,
  StyledFaqHeroSub,
  StyledFaqHeroTitle,
  StyledForm,
  StyledNeedHelpActions,
  StyledNeedHelpBody,
  StyledNeedHelpCard,
  StyledNeedHelpChat,
  StyledNeedHelpDesc,
  StyledNeedHelpHours,
  StyledNeedHelpTitle,
  StyledNeedHelpWhatsApp,
  StyledQuestionAnswer,
  StyledQuestionHeader,
  StyledQuestionItem,
} from './styles';

const SupportAndFaqPage: React.FC = () => {
  const { setHeaderTitle } = useHeaderTitleContext();
  const intl = useIntl();
  const history = useHistory();
  const [search, setSearch] = useState<string>('');
  const [openKey, setOpenKey] = useState<string | null>(null);

  useEffect(() => {
    setHeaderTitle(
      intl.formatMessage({
        id: 'sidebar.settings.supportAndFaq',
        defaultMessage: 'Support & FAQ',
      })
    );
  }, [intl, setHeaderTitle]);

  const filtered = useMemo(() => {
    if (!search) return FAQ_ITEMS;
    const q = search.toLowerCase();
    return FAQ_ITEMS.map((cat) => {
      const matchesCategory =
        cat.heading.toLowerCase().includes(q) ||
        cat.description.toLowerCase().includes(q);
      const matchingQuestions = cat.questions.filter((qi) =>
        qi.question.toLowerCase().includes(q)
      );
      if (matchesCategory) return cat;
      if (matchingQuestions.length === 0) return null;
      return { ...cat, questions: matchingQuestions };
    }).filter((x): x is typeof FAQ_ITEMS[number] => x !== null);
  }, [search]);

  const totalQuestions = useMemo(
    () => FAQ_ITEMS.reduce((acc, c) => acc + c.questions.length, 0),
    []
  );

  return (
    <>
      {/* "Need a hand?" green contact card */}
      <StyledNeedHelpCard>
        <StyledNeedHelpBody>
          <StyledNeedHelpTitle>
            <FormattedMessage
              id='faq.needAHand'
              defaultMessage='Need a hand?'
            />
          </StyledNeedHelpTitle>
          <StyledNeedHelpDesc>
            <FormattedMessage
              id='faq.needAHandDesc'
              defaultMessage="Talk to the Sandoog Client Relations team — we're here to help with anything."
            />
          </StyledNeedHelpDesc>
          <StyledNeedHelpHours>
            <FormattedMessage
              id='faq.supportHours'
              defaultMessage='Available Saturday – Thursday · 8 am – 6 pm'
            />
          </StyledNeedHelpHours>
        </StyledNeedHelpBody>
        <StyledNeedHelpActions>
          <StyledNeedHelpWhatsApp
            href='https://wa.me/9647823003004'
            target='_blank'
            rel='noopener noreferrer'
          >
            <IconWhatsApp />
            <FormattedMessage id='faq.whatsapp' defaultMessage='WhatsApp' />
          </StyledNeedHelpWhatsApp>
          <StyledNeedHelpChat
            href={routes.CONTACT_US}
            onClick={(e) => {
              e.preventDefault();
              history.push(routes.CONTACT_US);
            }}
          >
            <IconSupport />
            <FormattedMessage id='faq.startChat' defaultMessage='Start a chat' />
          </StyledNeedHelpChat>
        </StyledNeedHelpActions>
      </StyledNeedHelpCard>

      <StyledFaqHero>
        <StyledFaqHeroTitle>
          <FormattedMessage
            id='faq.sandoogFrequentlyAskedQuestions'
            defaultMessage='How can we help?'
          />
        </StyledFaqHeroTitle>
        <StyledFaqHeroSub>
          <FormattedMessage
            id='faq.description'
            defaultMessage='Find quick answers below. Anything we miss, the Sandoog Client Relations team is one click away.'
          />
        </StyledFaqHeroSub>
      </StyledFaqHero>

      <StyledToolbar>
        <StyledToolbarSearch>
          <StyledForm onSubmit={(e) => e.preventDefault()}>
            <Input
              mode={FieldMode.Tertiary}
              placeholder={intl.formatMessage({
                id: 'search.searchFaq',
                defaultMessage: `Search ${totalQuestions} answers…`,
              })}
              isRounded
              prefixComponent={<IconSearch />}
              onChange={({ target }) => setSearch(target.value)}
              value={search}
            />
          </StyledForm>
        </StyledToolbarSearch>
      </StyledToolbar>

      {filtered.length === 0 ? (
        <StyledFaqEmpty>
          {intl.formatMessage({ id: 'faq.noSearchResults', defaultMessage: 'No answers matched “{query}”. Try a different phrase, or contact support below.' }, { query: search })}
        </StyledFaqEmpty>
      ) : (
        filtered.map((cat) => (
          <StyledCategory key={cat.key}>
            <StyledCategoryTitle>{cat.heading}</StyledCategoryTitle>
            {cat.description ? (
              <StyledCategoryDescription>
                {cat.description}
              </StyledCategoryDescription>
            ) : null}
            {cat.questions.map((q) => {
              const compoundKey = `${cat.key}-${q.key}`;
              const isOpen = openKey === compoundKey;
              return (
                <StyledQuestionItem key={q.key}>
                  <StyledQuestionHeader
                    type='button'
                    open={isOpen}
                    aria-expanded={isOpen}
                    onClick={() =>
                      setOpenKey((current) =>
                        current === compoundKey ? null : compoundKey
                      )
                    }
                  >
                    <span>{q.question}</span>
                    <IconChevronDown />
                  </StyledQuestionHeader>
                  <StyledQuestionAnswer open={isOpen} aria-hidden={!isOpen}>
                    <div>{q.answer}</div>
                  </StyledQuestionAnswer>
                </StyledQuestionItem>
              );
            })}
          </StyledCategory>
        ))
      )}

      <StyledFaqContactCTA>
        <div>
          <strong>
            <FormattedMessage
              id='faq.stillNeedHelp'
              defaultMessage='Still need help?'
            />
          </strong>
          <small>
            <FormattedMessage
              id='faq.contactPrompt'
              defaultMessage='Talk to a person from the Client Relations team — usually replies within the hour.'
            />
          </small>
        </div>
        <a
          href={routes.CONTACT_US}
          onClick={(e) => {
            e.preventDefault();
            history.push(routes.CONTACT_US);
          }}
        >
          <FormattedMessage
            id='faq.contactCta'
            defaultMessage='Contact us'
          />
        </a>
      </StyledFaqContactCTA>
    </>
  );
};

export default SupportAndFaqPage;
